import { createContext, useContext, useState, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import { paymentApi } from '../services/paymentApi';

const AuthContext = createContext();

export const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null); // Added lastName state
  const [email, setEmail] = useState(null);
  const [lastFreeResearch, setLastFreeResearch] = useState('');
  const [sub, setSub] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionUntil, setSubscriptionUntil] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [subscriptionCanceledDate, setSubscriptionCanceledDate] = useState(null);
  const [credits, setCredits] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const loadUserFromToken = async (idToken) => {
    try {
      const tokenParts = idToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const userEmail = payload.email;
      const groups = payload['cognito:groups'] || [];
      const userAttributes = {};
      
      Object.keys(payload).forEach(key => {
        userAttributes[key] = payload[key];
      });
      
      setUser({
        email: userEmail,
        groups,
        attributes: userAttributes
      });
      
      setFirstName(userAttributes['given_name'] || null);
      setEmail(userEmail);
      setLastFreeResearch(userAttributes['custom:last_free_research'] || '');
      setSub(userAttributes['sub'] || null);
      setCredits(Number(userAttributes['custom:credits'] || 0));
      
      const subUntil = userAttributes['custom:subscribe_end'] || null;
      setSubscriptionUntil(subUntil);
      
      const subType = userAttributes['custom:subscription'] || '';
      setSubscriptionType(subType);
      
      setSubscriptionCanceledDate(userAttributes['custom:subs_cancel_at'] || null);
      
      const now = new Date();
      const isActive = subUntil && new Date(subUntil) > now;
      setHasActiveSubscription(isActive && subType === 'SUB');
      
      setIsAdmin(userAttributes['custom:is_admin'] === 'true' || 
                (groups && groups.includes('Admin')) || 
                userEmail.endsWith('@instantflows.com'));
                
      return userAttributes;
    } catch (error) {
      console.error('Error parsing token:', error);
      throw error;
    }
  };

  const setCognitoSession = (idToken, refreshToken) => {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const userEmail = payload.email;
      
      console.log("Setting up session for Google user:", userEmail);
      
      const keyPrefix = `CognitoIdentityServiceProvider.${process.env.REACT_APP_COGNITO_CLIENT_ID}`;
      
      localStorage.setItem(`${keyPrefix}.LastAuthUser`, userEmail);
      localStorage.setItem(`${keyPrefix}.${userEmail}.idToken`, idToken);
      localStorage.setItem(`${keyPrefix}.${userEmail}.refreshToken`, refreshToken);
      localStorage.setItem(`${keyPrefix}.${userEmail}.accessToken`, idToken);
      
      localStorage.setItem(`${keyPrefix}.${userEmail}.userData`, JSON.stringify({
        Username: userEmail,
        Pool: {
          UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
          ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
        }
      }));
      
      localStorage.setItem(`${keyPrefix}.${userEmail}.clockDrift`, '0');
      localStorage.setItem(`${keyPrefix}.${userEmail}.tokenScopesString`, 'phone openid profile email aws.cognito.signin.user.admin');
      
      console.log("Cognito session storage set up successfully");
    } catch (error) {
      console.error("Error setting up Cognito session:", error);
    }
  };

  const completeNewPassword = (cognitoUser, newPassword, userAttributes = {}) => { // Modified to accept userAttributes
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(
        newPassword,
        userAttributes, // Pass the user attributes here
        {
          onSuccess: async (session) => {
            try {
              await loadUserFromToken(session.getIdToken().getJwtToken());
              resolve(session);
            } catch (err) {
              reject(err);
            }
          },
          onFailure: (err) => reject(err)
        }
      );
    });
  };

  const login = (email, password) => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async session => {
          try {
            await loadUserFromToken(session.getIdToken().getJwtToken());
            resolve(session);
          } catch (error) {
            reject(error);
          }
        },
        onFailure: err => {
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Surface the challenge to the caller
          resolve({
            challenge: 'NEW_PASSWORD_REQUIRED',
            cognitoUser,
            userAttributes,
            requiredAttributes
          });
        }
      });
    });
  };

  useEffect(() => {
    const loadUser = async () => {
      console.log("Attempting to load user session...");
      const cognitoUser = userPool.getCurrentUser();
      console.log("Current user from Cognito:", cognitoUser);
      
      if (cognitoUser) {
        try {
          const session = await new Promise((resolve, reject) => {
            cognitoUser.getSession((err, session) => {
              if (err) {
                console.error('Session error:', err);
                reject(err);
              } else {
                console.log("Session retrieved successfully:", session);
                resolve(session);
              }
            });
          });

          if (session) {
            const groups = session.getIdToken().payload['cognito:groups'] || [];
            const userEmail = session.getIdToken().payload.email;
            const tokenPayload = session.getIdToken().payload;
            
            console.log("Session token payload:", tokenPayload);
            
            const userAttributes = {};
            Object.keys(tokenPayload).forEach(key => {
              userAttributes[key] = tokenPayload[key];
            });
            
            setUser({
              ...cognitoUser,
              email: userEmail,
              groups,
              attributes: userAttributes
            });
            
            setFirstName(userAttributes['given_name'] || null);
            setEmail(userEmail);
            setLastFreeResearch(userAttributes['custom:last_free_research'] || '');
            setSub(userAttributes['sub'] || null);
            setCredits(Number(userAttributes['custom:credits'] || 0));
            
            const subUntil = userAttributes['custom:subscribe_end'] || null;
            setSubscriptionUntil(subUntil);
            
            const subType = userAttributes['custom:subscription'] || '';
            setSubscriptionType(subType);
            
            setSubscriptionCanceledDate(userAttributes['custom:subs_cancel_at'] || null);
            
            const now = new Date();
            const isActive = subUntil && new Date(subUntil) > now;
            setHasActiveSubscription(isActive && subType === 'SUB');
            
            setIsAdmin(userAttributes['custom:is_admin'] === 'true' || 
                      (groups && groups.includes('Admin')) || 
                      userEmail.endsWith('@instantflows.com'));
            
            console.log("User session loaded from token payload");
          }
        } catch (error) {
          console.error('Error loading user from Cognito session:', error);
        }
      } else {
        console.log("No Cognito session found, checking for stored tokens");
        const idToken = localStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (idToken && refreshToken) {
          console.log("Found stored tokens, attempting to restore session");
          try {
            setCognitoSession(idToken, refreshToken);
            await loadUserFromToken(idToken);
            console.log("Session restored from localStorage tokens");
          } catch (error) {
            console.error("Failed to restore session from tokens:", error);
          }
        } else {
          console.log("No stored tokens found, user is not logged in");
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  useEffect(() => {
    if (user?.email) {
      const isAdminEmail = user.email.toLowerCase().endsWith('@instantflows.com');
      const isAdminGroup = user.groups?.includes('Admin');
      const isAdminAttr = user.attributes?.['custom:is_admin'] === 'true';
      
      const newIsAdmin = isAdminEmail || isAdminGroup || isAdminAttr;
      setIsAdmin(newIsAdmin);

      if (isAdminEmail && !isAdminAttr) {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
          const attribute = new CognitoUserAttribute({
            Name: 'custom:is_admin',
            Value: 'true'
          });
          
          cognitoUser.updateAttributes([attribute], (err) => {
            if (err) {
              // console.error('Error updating admin attribute:', err);
            }
          });
        }
      }
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const updateUserAttributes = async () => {
    try {
      const userDetails = await paymentApi.getUserDetails();
      
      setCredits(parseInt(userDetails.credits || '0'));
      setSubscriptionCanceledDate(userDetails.subscriptionCanceledDate || null);
      setSubscriptionUntil(userDetails.subscriptionUntil || null);
      setSubscriptionType(userDetails.subscriptionType || null);
      setHasActiveSubscription(!!userDetails.subscriptionUntil && 
                               new Date(userDetails.subscriptionUntil) > new Date());
      
      return userDetails;
    } catch (error) {
      throw error;
    }
  };

  const signUp = (email, password, firstName, lastName) => {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'custom:is_admin', Value: 'false' }),
        new CognitoUserAttribute({ Name: 'custom:last_free_research', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:subscribe_end', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:last_used_app', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:subscription', Value: 'FREE' }),
        new CognitoUserAttribute({ Name: 'custom:credits', Value: '3' })
      ];

      userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const loginWithGoogle = async (googleToken) => {
    try {
      console.log("Sending Google token to backend:", googleToken ? "Token received (first 20 chars): " + googleToken.substring(0, 20) + "..." : "No token");
      
      if (!googleToken) {
        throw new Error("No token provided");
      }
      
      console.log("Making fetch request to API:", process.env.REACT_APP_API_URL + "/google-auth");
      
      const startTime = Date.now();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ idToken: googleToken })
      });
      const endTime = Date.now();
      
      console.log(`Response received in ${endTime - startTime}ms, status:`, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Authentication successful:", data);
      
      try {
        const tokenPayload = JSON.parse(atob(data.idToken.split('.')[1]));
        console.log("Token payload:", tokenPayload);
        
        const userEmail = tokenPayload.email;
        const groups = tokenPayload['cognito:groups'] || [];
        
        setUser({
          email: userEmail,
          groups,
          attributes: tokenPayload
        });
        
        setFirstName(tokenPayload.given_name || null);
        setEmail(userEmail);
        setSub(tokenPayload.sub || null);
        setLastFreeResearch(tokenPayload['custom:last_free_research'] || '');
        setCredits(Number(tokenPayload['custom:credits'] || 0));
        setSubscriptionUntil(tokenPayload['custom:subscribe_end'] || null);
        setSubscriptionType(tokenPayload['custom:subscription'] || '');
        setSubscriptionCanceledDate(tokenPayload['custom:subs_cancel_at'] || null);
        setIsAdmin(tokenPayload['custom:is_admin'] === 'true' || 
                  (groups && groups.includes('Admin')) || 
                  userEmail.endsWith('@instantflows.com'));
                  
        console.log("User state set directly from token");
      } catch (tokenError) {
        console.error("Error extracting data from token:", tokenError);
      }
      
      if (data.idToken && data.refreshToken) {
        setCognitoSession(data.idToken, data.refreshToken);
        localStorage.setItem('idToken', data.idToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      return data;
    } catch (error) {
      console.error("Google login error:", error);
      throw error;
    }
  };

  const logout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
      setUser(null);
      setIsAdmin(false);
      setFirstName(null);
      setLastName(null); // Reset lastName on logout
      setEmail(null);
      setLastFreeResearch('');
      setSub(null);
      setSubscriptionUntil(null);
      setSubscriptionType(null);
      setCredits(0);
      setHasActiveSubscription(false);
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
    }
  };

  const verifyEmail = (email, code) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const forgotPassword = email => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: err => reject(err)
      });
    });
  };

  const confirmPassword = (email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: err => reject(err)
      });
    });
  };

  const changePassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user logged in'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          reject(new Error('User is not authenticated'));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    });
  };

  const deleteAccount = async () => {
    try {
      await paymentApi.deleteAccount();
      
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      
      setUser(null);
      setFirstName(null);
      setLastName(null); // Reset lastName on account deletion
      setEmail(null);
      setLastFreeResearch('');
      setSub(null);
      setIsAdmin(false);
      setSubscriptionUntil(null);
      setSubscriptionType(null);
      setSubscriptionCanceledDate(null);
      setCredits(0);
      setHasActiveSubscription(false);
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationCode = (email) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        firstName,
        lastName, // Added lastName to the context
        email,
        sub,
        lastFreeResearch,
        loading,
        signUp,
        login,
        completeNewPassword, // Added export for completeNewPassword
        loginWithGoogle,
        logout,
        verifyEmail,
        forgotPassword,
        confirmPassword,
        changePassword,
        subscriptionType,
        hasActiveSubscription,
        subscriptionUntil,
        subscriptionCanceledDate,
        credits,
        updateUserAttributes,
        deleteAccount,
        resendVerificationCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);